import React from 'react';
import './ReplyView.scss';
import TaskMessaging from "../../../ws/TaskMessaging";
import { GetLocale, LoadingView, withAppStyle, WithAppStyleProps } from '@cuatroochenta/co-admin-react-library';
import { withTranslation, WithTranslation } from 'react-i18next';
import { translate } from '../../../../I18n/i18n';
import { Conversation } from '../../../model/domain/messaging';

interface ReplyViewProps extends WithTranslation, WithAppStyleProps {
    conversation: Conversation;
    onClose: () => void;
    onSendMessage: () => void;
    onLoadMoreMessages: () => void;
    loading: boolean;
}

interface ReplyViewState {
    newMessage: string;
    error: Error | null;
    loading: boolean;
    showScrollToBottom: boolean;
    isSending: boolean;
}

class ReplyView extends React.Component<ReplyViewProps, ReplyViewState> {
    private textareaRef = React.createRef<HTMLTextAreaElement>();
    private messagesContainerRef = React.createRef<HTMLDivElement>();
    private isAtBottom = true;

    constructor(props: ReplyViewProps) {
        super(props);
        this.state = {
            newMessage: '',
            error: null,
            loading: false,
            showScrollToBottom: false,
            isSending: false
        };
    }

    componentDidMount() {
        this.scrollToBottom();
        if (this.messagesContainerRef.current) {
            this.messagesContainerRef.current.addEventListener('scroll', this.handleScroll, true);
        }
    }

    componentWillUnmount() {
        if (this.messagesContainerRef.current) {
            this.messagesContainerRef.current.removeEventListener('scroll', this.handleScroll, true);
        }
    }

    componentDidUpdate(prevProps: ReplyViewProps) {
        if (this.props.conversation.messages.length !== prevProps.conversation.messages.length) {
            if (this.isAtBottom) {
                this.scrollToBottom();
            }
        }
    }

    scrollToBottom = () => {
        if (this.messagesContainerRef.current) {
            const container = this.messagesContainerRef.current;
            container.scrollTop = container.scrollHeight;
        }
    };

    handleScroll = () => {
        const { conversation, loading, onLoadMoreMessages } = this.props;
        const { messages } = conversation;
        const container = this.messagesContainerRef.current;

        if (!container || loading) return;

        const isAtTop = container.scrollTop <= 50;
        const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 5;
        const isScrollingUp = container.scrollTop < container.scrollHeight - container.clientHeight;


        this.isAtBottom = isAtBottom;
        this.setState({ showScrollToBottom: isScrollingUp && !isAtBottom });

        if (isAtTop && !loading) {
            this.setState({ loading: true });
            onLoadMoreMessages();
        } else if (isAtBottom && !loading) {
            this.scrollToBottom();
        }
    };

    handleTextChange = () => {
        const textarea = this.textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    };

    handleMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            newMessage: event.target.value,
        });
    };

    handleClose = () => {
        this.setState({ newMessage: '' });
        this.props.onClose();
    };

    handleSendMessage = () => {
        const { newMessage } = this.state;
        const { conversation, onSendMessage } = this.props;

        if (!newMessage.trim()) return;
        this.setState({ isSending: true });

        new TaskMessaging()
            .replyConversation(conversation.id, newMessage)
            .then(() => {
                console.log('Mensaje enviado exitosamente.');
                this.setState({ newMessage: '', loading: false, isSending: false });
                onSendMessage();
            })
            .catch((error: Error) => {
                console.error('Error al enviar el mensaje:', error);
                this.setState({ error, loading: false, isSending: false });
            });
    };

    private formatDate(dateString: string): string {
        const date = new Date(dateString);
        const datePart = date.toLocaleDateString(GetLocale());
        const timePart = date.toLocaleTimeString(GetLocale(), { hour: '2-digit', minute: '2-digit' });
        return `${datePart} ${timePart}`;
    }

    render() {
        const { conversation, onClose, loading } = this.props;
        const { newMessage, showScrollToBottom, isSending } = this.state;
        const { t, appStyles } = this.props;

        return (
            <>
                <div
                    className="sidebar-button"
                    onClick={this.handleClose}
                    style={{
                        cursor: 'pointer',
                        width: '40px',
                        height: '40px',
                        backgroundColor: appStyles.primaryColor,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <i
                        className="fa-solid fa-chevron-left font-awesome-icon undefined"
                        data-mui-internal-clone-element="true"
                        style={{
                            color: "white",
                            fontSize: '20px',
                        }}
                    ></i>
                </div>
                <div className='reply-container'>

                    <div className="reply-view">

                        <div className="content-container" style={{ borderColor: appStyles.primaryColor }}>
                            <div className="header-container">
                                <h4 style={{ marginTop: '0px !important' }}>{translate(t, 'messages.TR_ISSUE_LABEL')} {conversation.subject}</h4>
                                <p><strong>{translate(t, 'messages.TR_SENDER_LABEL')}</strong> <label>{conversation.sender_name ? conversation.sender_name : conversation.sender_email}</label></p>
                                <p>
                                    <strong>{translate(t, 'messages.TR_RESIDENTS_LABEL')} </strong>
                                    <label>{conversation.userResidentsFullNames.length > 0
                                        ? conversation.userResidentsFullNames.join(', ')
                                        : translate(t, 'messages.TR_NO_RESIDENTS_LABEL')}
                                    </label>
                                </p>
                            </div>

                            <div className="messages-container" ref={this.messagesContainerRef}>
                                {loading && (
                                    <div className="spinner-container">
                                        <LoadingView loading={true} />
                                    </div>
                                )}
                                {conversation.messages
                                    .slice()
                                    .reverse()
                                    .map((message) => (
                                        <div
                                            key={message.id}
                                            className={`message ${message.sender_type === "user" ? "user" : "professional"}`}
                                        >
                                            <p>
                                                {message.body}
                                                <span className="message-date">
                                                    {this.formatDate(message.created_at)}
                                                </span>
                                            </p>

                                        </div>
                                    ))}
                                {showScrollToBottom && (
                                    <button
                                        className="scroll-to-bottom-button show"
                                        onClick={this.scrollToBottom}
                                    >
                                        &#8595;
                                    </button>
                                )}
                            </div>
                            <div className="input-container">
                                <textarea
                                    ref={this.textareaRef}
                                    className="message-input"
                                    placeholder={translate(t, 'news.TR_PLACEHOLDER_CREATE_NEWS_LABEL')}
                                    onInput={this.handleTextChange}
                                    value={newMessage}
                                    onChange={this.handleMessageChange}
                                />
                                <button
                                    className="send-button"
                                    style={{ backgroundColor: appStyles.primaryColor }}
                                    onClick={this.handleSendMessage}
                                    disabled={isSending}
                                >
                                    {isSending
                                        ? '...'
                                        : translate(t, 'messages.TR_SEND_LABEL')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default withAppStyle(withTranslation()(ReplyView));
